<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { Select, InputNumber, ToggleSwitch, Fieldset, Button, Message } from 'primevue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useSelectedProductionLineStore } from '../stores/selectedProductionLineStore';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { useSelectedShapeStore } from '../stores/selectedShapeStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { storagePlaceLabel } from 'shared/storageAreas/domain/computePlaceDisplayText';
import { useProductStore } from 'shared/products/stores/productStore';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const productionLineTranslations = computed(() => translations.value.productionLine);

const store = useSelectedProductionLineStore();
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const selectedShapeStore = useSelectedShapeStore();
const storageAreaStore = useStorageAreaStore();
const shapeStore = useShapeStore();
const productStore = useProductStore();
const packagingStore = usePackagingStore();
const packageTypeStore = usePackageTypeStore();
const featureFlagStore = useFeatureFlagStore();

const simulationVisible = computed(() => featureFlagStore.isSimulationOptionsEnabled());

// A child output/intake area can only be added to a production line that has
// already been saved (so it has an id its children can reference).
const lineId = computed(() => selectedStorageAreaStore.storageAreaId);
const canAddChild = computed(() => !!lineId.value);

function centerCoordinate(feature: GeoJSON.Feature | null | undefined) {
  if (!feature) return null;
  const geometry = feature.geometry;
  let ring: GeoJSON.Position[] | null = null;
  if (geometry.type === 'Polygon') {
    ring = geometry.coordinates[0] ?? null;
  } else if (geometry.type === 'MultiPolygon') {
    ring = geometry.coordinates[0]?.[0] ?? null;
  }
  if (!ring || ring.length === 0) return null;

  let sumX = 0;
  let sumY = 0;
  for (const point of ring) {
    sumX += point[0]!;
    sumY += point[1]!;
  }
  return { longitude: sumX / ring.length, latitude: sumY / ring.length };
}

function addChildArea(type: StorageAreaType.PackageOutput | StorageAreaType.PackageIntake) {
  const id = lineId.value;
  if (!id) return;

  const shape = shapeStore.getByStorageAreaId(id)[0];
  const coordinate = centerCoordinate(shape?.shapeGeoJson);
  if (!coordinate) return;

  selectedShapeStore.create({
    coordinate,
    width: 10,
    length: 10,
    storageArea: {
      type,
      allowedTypes: [StorageAreaType.PackageOutput, StorageAreaType.PackageIntake],
      productionLineId: id,
    },
  });
}

const {
  simulationEnabled,
  simulationIntervalSeconds,
  simulationOutputPlaceId,
  simulationProductId,
  simulationPiecesCount,
  simulationPackagingId,
  simulationPackageTypeId,
  simulationLength,
  simulationWidth,
  simulationHeight,
} = storeToRefs(store);

const productOptions = computed(() =>
  productStore.sortedByName.map((p) => ({ label: p.name, value: p.id })),
);

const packagingOptions = computed(() =>
  packagingStore.sortedByName.map((p) => ({ label: p.name, value: p.id })),
);

const packageTypeOptions = computed(() =>
  packageTypeStore.sortedByName.map((p) => ({ label: p.name, value: p.id })),
);

// Output places belonging to output areas of the currently selected production line.
const outputPlaceOptions = computed(() => {
  const lineId = selectedStorageAreaStore.storageAreaId;
  if (!lineId) return [];

  return storageAreaStore.storageAreas
    .filter(
      (area) =>
        area.type === StorageAreaType.PackageOutput &&
        area.packagePlaceData?.productionLineId === lineId,
    )
    .flatMap((area) =>
      (area.packagePlaceData?.places ?? []).map((place) => ({
        label: `${area.name} #${storagePlaceLabel(place)}`,
        value: place.id,
      })),
    );
});
</script>

<template>
  <div class="production-line-panel-root">
    <div class="add-child-actions">
      <label class="add-child-label">{{ productionLineTranslations.areas }}</label>
      <div class="add-child-buttons">
        <Button size="small" icon="pi pi-plus" :label="productionLineTranslations.addOutputArea"
          :disabled="!canAddChild" @click="addChildArea(StorageAreaType.PackageOutput)"></Button>
        <Button size="small" icon="pi pi-plus" :label="productionLineTranslations.addIntakeArea"
          :disabled="!canAddChild" @click="addChildArea(StorageAreaType.PackageIntake)"></Button>
      </div>
      <Message v-if="!canAddChild" severity="info" variant="simple">{{ productionLineTranslations.saveLineFirst }}
      </Message>
    </div>

    <Fieldset v-if="simulationVisible" :legend="productionLineTranslations.simulation" class="production-line-panel">
      <div class="toggle-field">
        <ToggleSwitch v-model:modelValue="simulationEnabled"></ToggleSwitch>
        <label>{{ productionLineTranslations.simulationEnabled }}</label>
      </div>

      <template v-if="simulationEnabled">
        <div class="field">
          <label>{{ productionLineTranslations.intervalSeconds }}</label>
          <InputNumber fluid v-model:model-value="simulationIntervalSeconds" :min="1"></InputNumber>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.outputPlace }}</label>
          <Select v-model:model-value="simulationOutputPlaceId" :options="outputPlaceOptions" option-label="label"
            option-value="value" show-clear></Select>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.product }}</label>
          <Select v-model:model-value="simulationProductId" :options="productOptions" option-label="label"
            option-value="value" filter show-clear></Select>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.piecesCount }}</label>
          <InputNumber fluid v-model:model-value="simulationPiecesCount" :min="0"></InputNumber>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.packaging }}</label>
          <Select v-model:model-value="simulationPackagingId" :options="packagingOptions" option-label="label"
            option-value="value" filter show-clear></Select>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.packageType }}</label>
          <Select v-model:model-value="simulationPackageTypeId" :options="packageTypeOptions" option-label="label"
            option-value="value" filter show-clear></Select>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.length }}</label>
          <InputNumber fluid v-model:model-value="simulationLength" :min="0"></InputNumber>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.width }}</label>
          <InputNumber fluid v-model:model-value="simulationWidth" :min="0"></InputNumber>
        </div>

        <div class="field">
          <label>{{ productionLineTranslations.height }}</label>
          <InputNumber fluid v-model:model-value="simulationHeight" :min="0"></InputNumber>
        </div>
      </template>
    </Fieldset>
  </div>
</template>

<style scoped>
.production-line-panel-root {
  display: grid;
  row-gap: 0.75rem;
}

.add-child-actions {
  display: grid;
  row-gap: 0.5rem;
}

.add-child-label {
  font-weight: 600;
}

.add-child-buttons {
  display: flex;
  column-gap: 0.5rem;
  flex-wrap: wrap;
}

.production-line-panel {
  display: grid;
  row-gap: 0.5rem;
}

.field {
  display: grid;
  row-gap: 0.25rem;
}

.toggle-field {
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
}
</style>
